// THE CALL PANEL'S CUSTOMER, ACROSS EVERY CHANNEL (Stage 4.5, the owner's design 2026-10-26: "upgrade the panel"). Three pieces
// the panel's columns take in, beside what they always showed: the customer as the CRM knows them (column 1), a WhatsApp
// conversation as a history row beside the calls (column 2), and what is open for them now (column 3, above the ticket). Facts
// read from the records (server/ai/callerBriefing.js), already cut to what this agent may see.
import React from "react";
import { useTranslation } from "react-i18next";
import { Box, Typography } from "@mui/material";
import { BellOff, CalendarClock, Mail, MessageCircle, Phone, Reply, ShoppingBag, Star } from "lucide-react";

import { dateTimeText, dayAndTime } from "../../../utils/times";
import { stageKey } from "./briefingMeta";

const ui = { fontFamily: "var(--font-ui)" };
const caption = { ...ui, fontSize: "var(--t-caption)" };
const pill = (fg, bg) => ({ ...caption, display: "inline-flex", alignItems: "center", gap: "4px", px: "8px", py: "1px", borderRadius: "var(--r-pill)", fontWeight: 600, color: fg, bgcolor: bg });

// Column 1: the CRM's own facts about them — status, VIP, do-not-contact, tags, email, the number it holds.
export function ContactFacts({ contact, showName = false }) {
  const { t } = useTranslation("handoff");
  if (!contact) return null;
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "6px" }}>
      {showName && contact.name && <Typography dir="auto" sx={{ ...ui, fontSize: "var(--t-body-sm)", fontWeight: 700, color: "var(--ink-900)" }}>{contact.name}</Typography>}
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
        {contact.status && <Box component="span" sx={pill("var(--c-violet)", "var(--c-violet-bg)")}>{contact.status}</Box>}
        {contact.vip && <Box component="span" sx={pill("var(--warning)", "var(--warning-bg)")}><Star size={11} aria-hidden="true" /> {t("briefing.vip")}</Box>}
        {contact.doNotContact && <Box component="span" sx={pill("var(--danger)", "var(--danger-bg)")}><BellOff size={11} aria-hidden="true" /> {t("briefing.doNotContact")}</Box>}
        {(contact.tags ?? []).map((tag) => <Box component="span" key={tag} dir="auto" sx={pill("var(--ink-600)", "var(--surface-2)")}>{tag}</Box>)}
      </Box>
      {contact.email && (
        <Box sx={{ display: "flex", alignItems: "center", gap: "6px", color: "var(--ink-500)" }}>
          <Mail size={13} aria-hidden="true" />
          <Typography sx={{ ...caption, color: "var(--ink-600)", overflowWrap: "anywhere" }}>{contact.email}</Typography>
        </Box>
      )}
      {contact.phone && (
        <Box sx={{ display: "flex", alignItems: "center", gap: "6px", color: "var(--ink-500)" }}>
          <Phone size={13} aria-hidden="true" />
          <Typography dir="ltr" sx={{ ...caption, color: "var(--ink-600)", fontFamily: "var(--font-mono, monospace)" }}>{contact.phone}</Typography>
        </Box>
      )}
    </Box>
  );
}

// Column 2: a WhatsApp conversation beside the calls — when, how it ended (or that it is still going, and waits on a person),
// and its record's one line when this agent may read summaries.
export function WhatsAppRow({ wa, last = false }) {
  const { t } = useTranslation("handoff");
  const state = wa.ongoing
    ? (wa.needsPerson ? t("briefing.waitsOnPerson") : t("briefing.ongoing"))
    : (wa.outcome ? t(`interactions:outcome.${wa.outcome}`, { defaultValue: wa.outcome }) : null);
  return (
    <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: "var(--sp-2)", py: "6px", borderBottom: last ? "none" : "1px solid var(--line-100)", minWidth: 0 }}>
      <Typography sx={{ ...caption, color: "var(--ink-500)", minWidth: 100, fontFamily: "var(--font-mono, monospace)", flexShrink: 0 }}>{dateTimeText(wa.at)}</Typography>
      <Box sx={{ display: "inline-flex", alignItems: "center", gap: "4px", minWidth: 48, color: "var(--success)", flexShrink: 0 }}>
        <MessageCircle size={13} aria-hidden="true" />
        <Typography component="span" sx={{ ...caption, color: "var(--ink-600)" }}>{t("briefing.channel_whatsapp")}</Typography>
      </Box>
      {state && <Box component="span" sx={{ ...pill(wa.ongoing && wa.needsPerson ? "var(--warning)" : "var(--ink-600)", wa.ongoing && wa.needsPerson ? "var(--warning-bg)" : "var(--surface-2)"), flexShrink: 0 }}>{state}</Box>}
      {wa.summary && (
        <Typography dir="auto" title={wa.summary} sx={{ ...caption, color: "var(--ink-500)", minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {wa.summary}
        </Typography>
      )}
    </Box>
  );
}

// Column 3: what is open for them now — orders, the next appointment, follow-ups promised — above the ticket's own block.
export function OpenItems({ open }) {
  const { t } = useTranslation("handoff");
  if (!open) return null;
  const rows = [
    ...(open.orders ?? []).map((o) => ({ key: o.ref, Icon: ShoppingBag, ref: o.ref, text: t(`orders:${stageKey(o)}`) })),
    ...(open.appointment ? [{ key: open.appointment.ref, Icon: CalendarClock, ref: open.appointment.ref,
      text: [dayAndTime(open.appointment.startAt), open.appointment.purpose].filter(Boolean).join(" · ") }] : []),
    ...(open.followUps ?? []).map((f) => ({ key: f.ref, Icon: Reply, ref: f.ref,
      text: [dayAndTime(f.dueAt), f.purpose].filter(Boolean).join(" · ") })),
  ];
  if (!rows.length) return null;
  return (
    <Box component="ul" aria-label={t("briefing.openNow")} sx={{ m: 0, p: 0, mb: "var(--sp-3)", display: "grid", gap: "6px" }}>
      {rows.map((row) => {
        const Icon = row.Icon;
        return (
          <Box component="li" key={row.key} sx={{ listStyle: "none", display: "flex", alignItems: "center", gap: "var(--sp-2)", minWidth: 0 }}>
            <Icon size={13} aria-hidden="true" color="var(--c-violet)" style={{ flexShrink: 0 }} />
            <Typography component="span" sx={{ ...caption, fontWeight: 700, color: "var(--ink-800)", fontFamily: "var(--font-mono, monospace)", flexShrink: 0 }}>{row.ref}</Typography>
            <Typography component="span" dir="auto" title={row.text} sx={{ ...caption, color: "var(--ink-600)", minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{row.text}</Typography>
          </Box>
        );
      })}
    </Box>
  );
}
